<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useQuery } from '@tanstack/vue-query';
import {
  unplannedPatientsApi, type UnplannedPatient,
} from '@/api/planning/unplanned-patients';
import { useUser } from '@/composables/useUser';
import { usePermissions } from '@/composables/usePermissions';
import { useGroupFilterStore } from '@/stores/group-filter';
import { careerLabel, careerMetadata } from '@/utils/career-metadata';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import { useCanSeeCoverageAlerts, useOpenCoverageAlertsCount } from '@/composables/useOpenCoverageAlerts';

const { user } = useUser();
const { hasPermission } = usePermissions();

const canSeeUnplannedPatients = computed(() => (
  hasPermission.value('requirements', 'index') &&
  !!user?.value?.companyCarePlanningEnabled
));
const canSeeMonthlyTracking = computed(() => (
  hasPermission.value('monthly_tracking', 'index') &&
  !!user?.value?.companyCarePlanningEnabled
));
const canSeeRoutePlans = computed(() => (
  hasPermission.value('route_plans', 'index') &&
  !!user?.value?.companyRoutePlansEnabled
));

const canSeeCoverageAlerts = useCanSeeCoverageAlerts();
const openCoverageAlerts = useOpenCoverageAlertsCount();

const { groupIds: groupFilterIds } = storeToRefs(useGroupFilterStore());

const {
  data: unplannedPatients, isPending,
} = useQuery({
  queryKey: ['planning', 'unplannedPatients', groupFilterIds],
  queryFn: () => unplannedPatientsApi.index(groupFilterIds.value),
  initialData: [] as UnplannedPatient[],
  enabled: canSeeUnplannedPatients,
});

function goToPatient(patientId: number) {
  window.location.href = `/patients/${patientId}?tab=requirements`;
}
</script>

<template>
  <v-container class="planning-container">
    <!-- Header -->
    <div class="d-flex align-center ga-3 mb-6">
      <v-avatar
        color="primary"
        variant="tonal"
        size="48"
      >
        <v-icon
          icon="mdi-clipboard-text-clock-outline"
          size="28"
        />
      </v-avatar>
      <div>
        <div class="text-h5 font-weight-bold">
          Planificación
        </div>
        <div class="text-body-2 text-medium-emphasis">
          Coordina los planes semanales, las rutas diarias y el seguimiento mensual.
        </div>
      </div>
    </div>

    <!-- Sections A + B: navigation cards -->
    <v-row
      v-if="canSeeMonthlyTracking || canSeeRoutePlans || canSeeCoverageAlerts"
      dense
      class="mb-2"
    >
      <v-col
        v-if="canSeeCoverageAlerts"
        cols="12"
        md="6"
      >
        <v-card
          rounded="lg"
          elevation="1"
          class="link-card h-100"
          href="/coverage_alerts"
        >
          <v-card-text class="pa-5 d-flex align-center ga-4">
            <v-avatar
              :color="openCoverageAlerts ? 'error' : 'grey'"
              variant="tonal"
              size="56"
            >
              <v-icon
                icon="mdi-bell-alert-outline"
                size="32"
              />
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">
                Alertas de cobertura
                <v-chip
                  v-if="openCoverageAlerts"
                  color="error"
                  size="small"
                  class="ml-2"
                >
                  {{ openCoverageAlerts }} abiertas
                </v-chip>
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Visitas planificadas que están por comenzar sin turno, y cómo se resolvieron.
              </div>
            </div>
            <v-btn
              :color="openCoverageAlerts ? 'error' : 'grey'"
              variant="tonal"
              icon="mdi-arrow-right"
              size="small"
              href="/coverage_alerts"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        v-if="canSeeMonthlyTracking"
        cols="12"
        md="6"
      >
        <v-card
          rounded="lg"
          elevation="1"
          class="link-card h-100"
          href="/monthly_tracking"
        >
          <v-card-text class="pa-5 d-flex align-center ga-4">
            <v-avatar
              color="success"
              variant="tonal"
              size="56"
            >
              <v-icon
                icon="mdi-chart-timeline-variant"
                size="32"
              />
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">
                Seguimiento mensual
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Compara los turnos presupuestados con los planificados y los reales.
              </div>
            </div>
            <v-btn
              color="success"
              variant="tonal"
              icon="mdi-arrow-right"
              size="small"
              href="/monthly_tracking"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        v-if="canSeeRoutePlans"
        cols="12"
        md="6"
      >
        <v-card
          rounded="lg"
          elevation="1"
          class="link-card h-100"
          href="/route_plans"
        >
          <v-card-text class="pa-5 d-flex align-center ga-4">
            <v-avatar
              color="info"
              variant="tonal"
              size="56"
            >
              <v-icon
                icon="mdi-routes"
                size="32"
              />
            </v-avatar>
            <div class="flex-grow-1">
              <div class="text-subtitle-1 font-weight-bold">
                Planificación de rutas
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Arma las rutas diarias para cada especialidad y aplícalas.
              </div>
            </div>
            <v-btn
              color="info"
              variant="tonal"
              icon="mdi-arrow-right"
              size="small"
              href="/route_plans"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Section C: Patients without weekly plans -->
    <v-card
      v-if="canSeeUnplannedPatients"
      rounded="lg"
      elevation="1"
      class="mt-4"
    >
      <v-card-text class="pa-4">
        <div class="d-flex align-center ga-2 mb-3">
          <v-icon
            icon="mdi-account-alert-outline"
            color="warning"
          />
          <span class="text-subtitle-1 font-weight-bold">
            Pacientes sin plan semanal
          </span>
          <v-chip
            v-if="!isPending && unplannedPatients.length > 0"
            size="small"
            color="warning"
            variant="tonal"
          >
            {{ unplannedPatients.length }}
          </v-chip>
          <v-spacer />
          <group-filter-select
            v-model="groupFilterIds"
            hide-details
            class="group-filter"
          />
        </div>

        <p class="text-body-2 text-medium-emphasis mb-3">
          Pacientes con requerimientos activos pero sin un plan semanal asignado.
          Haz click para ir al perfil y configurarlos.
        </p>

        <!-- Loading -->
        <template v-if="isPending">
          <v-skeleton-loader
            v-for="i in 3"
            :key="i"
            type="list-item-two-line"
          />
        </template>

        <!-- Empty -->
        <v-alert
          v-else-if="unplannedPatients.length === 0"
          type="success"
          variant="tonal"
          density="comfortable"
          icon="mdi-check-circle-outline"
        >
          Todos los pacientes activos tienen plan semanal asignado.
        </v-alert>

        <!-- List -->
        <v-list
          v-else
          density="compact"
          class="bg-transparent"
        >
          <v-list-item
            v-for="patient in unplannedPatients"
            :key="patient.patientId"
            class="px-2 unplanned-row"
            rounded="md"
            @click="goToPatient(patient.patientId)"
          >
            <template #prepend>
              <v-avatar
                color="primary"
                variant="tonal"
                size="36"
              >
                <v-icon icon="mdi-account" />
              </v-avatar>
            </template>
            <v-list-item-title class="text-subtitle-2 font-weight-medium">
              {{ patient.patientFullName }}
            </v-list-item-title>
            <v-list-item-subtitle class="d-flex ga-1 flex-wrap mt-1">
              <v-chip
                v-for="career in patient.unplannedCareers"
                :key="career"
                size="x-small"
                variant="tonal"
                :color="careerMetadata(career).color"
                :prepend-icon="careerMetadata(career).icon"
              >
                {{ careerLabel(career) }}
              </v-chip>
            </v-list-item-subtitle>
            <template #append>
              <v-icon
                icon="mdi-arrow-right"
                color="medium-emphasis"
              />
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.planning-container {
  max-width: 1400px;
}

.unplanned-row {
  transition: background-color .15s ease;
  cursor: pointer;
}

.unplanned-row:hover {
  background-color: rgba(var(--v-theme-primary), .04);
}

.link-card {
  transition: box-shadow .2s ease;
}

.link-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, .08) !important;
}

.group-filter {
  max-width: 280px;
}
</style>
